import type { OAuthConsentRequest } from '@/services/oauthService';

export interface RequestedAccess {
  /** A name from the shared icon set. */
  icon: string;
  text: string;
}

// What each OpenID Connect scope lets an application do, in the person's terms. Hydra advertises
// `offline` beside `offline_access`; both mean the same.
const OPENID_CONNECT: Record<string, RequestedAccess> = {
  openid: { icon: 'account', text: 'Confirm who you are' },
  offline_access: { icon: 'clock', text: 'Stay connected without asking you to sign in again' },
  offline: { icon: 'clock', text: 'Stay connected without asking you to sign in again' },
  profile: { icon: 'user', text: 'See your name' },
  email: { icon: 'send-out', text: 'See your email address' },
  address: { icon: 'location', text: 'See your postal address' },
  phone: { icon: 'chat', text: 'See your phone number' },
};

// Who the person is first, lasting access last.
const ORDER = ['openid', 'profile', 'email', 'address', 'phone'];
const LASTING = ['offline_access', 'offline'];

/**
 * What the application could do once allowed, one line each. The application's own API scopes
 * are described once, by the API they reach: what it may do there is chosen on the page.
 */
export function requestedAccess(request: OAuthConsentRequest): RequestedAccess[] {
  const access: RequestedAccess[] = [];
  const add = (scopes: string[]) => {
    for (const scope of scopes) {
      const known = OPENID_CONNECT[scope];
      if (request.scopes.includes(scope) && !access.some((item) => item.text === known.text)) {
        access.push(known);
      }
    }
  };
  add(ORDER);
  if (request.api_audience) {
    access.push({
      icon: 'share',
      text:
        request.delegable_permissions.length > 0
          ? `Use ${request.api_audience.name} on your behalf, only on what you choose in the next step`
          : `Use ${request.api_audience.name} on your behalf, in the organisation you choose`,
    });
  }
  add(LASTING);
  return access;
}

/** The application's name for people, or its client ID when it was registered without one. */
export function applicationName(request: { client_id: string; client_name: string }): string {
  return request.client_name.trim() || request.client_id;
}
